import { IBuildingEfficiencyData } from '@app/efficiency-analysis/models/buildings/building-efficiency-data';
import { ApiService } from './api.service';
import { BuildingEfficiencyAnalysis } from '@app/efficiency-analysis/models/buildings/building-efficiency-analysis';
import { environment } from '@env/environment';
import { EfficiencyAnalyses } from '@app/efficiency-analysis/models/buildings/efficiency-analyses';
import { EfficiencyAnalysisMeter } from '@app/efficiency-analysis/models/buildings/efficiency-analyses-meters';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BuildingMeter } from '@app/efficiency-analysis/models/buildings/building-meters';

@Injectable({
  providedIn: 'root',
})
export class BuildingEfficiencyService extends ApiService {
  private noKomKeyMunicipality: string[] = [];
  private komKeys: string;

  constructor(public http: HttpClient) {
    super(http);
    this.noKomKeyMunicipality = environment.noKomKeyMunicipality.split(';');
    this.komKeys = environment.allowedEnergyDataKomKeys.split(';').join("','");
  }

  public getBuildingsbyMunicipalityKey(municipalityKey: string): Observable<IBuildingEfficiencyData[]> {
    let url = `socrates/meters/api/municipalities/${municipalityKey}/buildings`;
    let filterQuery = '$filter=meters/any()';

    if (this.noKomKeyMunicipality.indexOf(municipalityKey) < 0) {
      filterQuery = `$filter=meters/any() and meters/any(m: m/komKey in ('${this.komKeys}') and m/meterNumber ne null and m/usageEndDate eq null)`;
    }
    const eaMetersSelectquery = '$select=guid,value,isSelected,meterNumber;$filter=meterNumber ne null';
    const metersSelectQuery = `$select=meterNumber,usageEndDate,komKey;$filter=meterNumber ne null and usageEndDate eq null and komKey in ('${this.komKeys}')`;
    const expandQuery = `$expand=efficiencyAnalyses($expand=efficiencyAnalysisMeters(${eaMetersSelectquery})),meters(${metersSelectQuery})&$expand=buildingType`;
    url += `?${filterQuery}&${expandQuery}`;
    return this.http.get<BuildingEfficiencyAnalysis[]>(this.endpoint(url)).pipe(
      map((buildings: BuildingEfficiencyAnalysis[]) => {
        const filterActiveBuildings = buildings.filter((building: BuildingEfficiencyAnalysis) => building.buildingType?.isActive ?? true);
        return this.buildingEfficiencyData(filterActiveBuildings, false);
      })
    );
  }

  public getDeactivatedBuildingsWithEA(municipalityKey: string): Observable<IBuildingEfficiencyData[]> {
    let url = `socrates/meters/api/municipalities/${municipalityKey}/buildings`;
    const eaMetersSelectquery = '$select=guid,value,isSelected,meterNumber;$filter=meterNumber ne null';
    const metersSelectQuery = '$select=meterNumber,usageEndDate,komKey;$filter=meterNumber ne null and usageEndDate ne null and komKey ne null';
    const expandQuery = `$expand=efficiencyAnalyses($expand=efficiencyAnalysisMeters(${eaMetersSelectquery})),meters(${metersSelectQuery})`;
    const filterQuery = `$filter=meters/all(m: m/komKey in ('${this.komKeys}')) eq false and efficiencyAnalyses/any() and efficiencyAnalyses/all(ea: ea/efficiencyAnalysisDate ne null and ea/electricityColorStatus ne null and ea/heatColorStatus ne null and ea/electricityComputedValue ne null and ea/heatComputedValue ne null)`;
    url += `?${filterQuery}&${expandQuery}`;
    return this.http
      .get<BuildingEfficiencyAnalysis[]>(this.endpoint(url))
      .pipe(map((buildings: BuildingEfficiencyAnalysis[]) => this.buildingEfficiencyData(buildings, true)));
  }

  public getBuildingEffieciencyByBuildingId(buildingId: string, municipalityKey: string): Observable<IBuildingEfficiencyData[]> {
    let url = `socrates/meters/api/municipalities/${municipalityKey}/buildings/${buildingId}`;
    const eaMetersSelectquery = '$select=guid,value,isSelected,meterNumber';
    const metersSelectQuery = '$select=meterNumber,usageEndDate,komKey';
    const expandQuery = `$expand=efficiencyAnalyses($expand=efficiencyAnalysisMeters(${eaMetersSelectquery})),meters(${metersSelectQuery})`;
    url += `?${expandQuery}`;
    return this.http
      .get<BuildingEfficiencyAnalysis[]>(this.endpoint(url))
      .pipe(map((buildings: BuildingEfficiencyAnalysis[]) => this.buildingEfficiencyData(buildings, false)));
  }

  private buildingEfficiencyData(buildings: BuildingEfficiencyAnalysis[], isBuildingDeactivated: boolean): IBuildingEfficiencyData[] {
    const buildingEfficiencyData: IBuildingEfficiencyData[] = [];
    buildings.forEach((building: BuildingEfficiencyAnalysis) => {
      if (building.efficiencyAnalyses.length > 0) {
        building.efficiencyAnalyses.forEach((ea: EfficiencyAnalyses) =>
          ea.efficiencyAnalysisMeters.map((eaMeter: EfficiencyAnalysisMeter) => {
            const meter = building.meters.find((m: BuildingMeter) => m.meterNumber === eaMeter.meterNumber);
            buildingEfficiencyData.push({
              averageElectricityConsumption: ea.averageElectricityConsumption,
              averageHeatConsumption: ea.averageHeatConsumption,
              buildingTypeId: building.buildingTypeId,
              connectionObjectNumber: building.connectionObjectNumber,
              creationDate: building.creationDate,
              description: building.description,
              district: building.district,
              districtHeatingConsumption: ea.districtHeatingConsumption,
              districtHeatingFromDate: ea.districtHeatingFromDate,
              districtHeatingToDate: ea.districtHeatingToDate,
              districtNumber: building.districtNumber,
              efficiencyAnalysisDate: ea.efficiencyAnalysisDate,
              efficiencyAnalysisEndDate: ea.efficiencyAnalysisEndDate,
              efficiencyAnalysisId: ea.guid,
              efficiencyAnalysisMetersId: eaMeter.guid,
              efficiencyAnalysisStartDate: ea.efficiencyAnalysisStartDate,
              efficiencyPercentageElectricity: ea.efficiencyPercentageElectricity,
              efficiencyPercentageHeat: ea.efficiencyPercentageHeat,
              electricityColorStatus: ea.electricityColorStatus,
              electricityComputedValue: ea.electricityComputedValue,
              electricityConsumption: ea.electricityConsumption,
              electricityFromDate: ea.electricityFromDate,
              electricityGuideValue: ea.electricityGuideValue,
              electricityHeatConsumption: ea.electricityHeatConsumption,
              electricityHeatFromDate: ea.electricityHeatFromDate,
              electricityHeatToDate: ea.electricityHeatToDate,
              electricityMaxDataRange: ea.electricityMaxDataRange,
              electricityMinDataRange: ea.electricityMinDataRange,
              electricityToDate: ea.electricityToDate,
              endDate: ea.endDate,
              energyCategory: building.energyCategory,
              floorArea: ea.floorArea,
              gpsModifiedBy: building.gpsModifiedBy,
              gpsModifyDate: building.gpsModifyDate,
              guid: building.guid,
              heatColorStatus: ea.heatColorStatus,
              heatComputedValue: ea.heatComputedValue,
              heatGuideValue: ea.heatGuideValue,
              heatingOilConsumption: ea.heatingOilConsumption,
              heatingOilFromDate: ea.heatingOilFromDate,
              heatingOilToDate: ea.heatingOilToDate,
              heatMaxDataRange: ea.heatMaxDataRange,
              heatMinDataRange: ea.heatMinDataRange,
              houseNumber: building.houseNumber,
              houseNumberAdditive: building.houseNumberAdditive,
              isBuildingDeactivated: isBuildingDeactivated,
              isElectricityValueSuggested: ea.isElectricityValueSuggested,
              isEnergyGuardNotificationActive: building.isEnergyGuardNotificationActive,
              isHeatValueSuggested: ea.isHeatValueSuggested,
              isSelected: eaMeter.isSelected,
              komKey: meter?.komKey ?? '',
              latitude: building.latitude,
              locationInfo: building.locationInfo,
              longitude: building.longitude,
              lpgConsumption: ea.lpgConsumption,
              lpgFromDate: ea.lpgFromDate,
              lpgToDate: ea.lpgToDate,
              meterNumber: eaMeter.meterNumber,
              modifyDate: building.modifyDate,
              modifyUser: building.modifyUser,
              municipality: building.municipality,
              municipalityId: building.municipalityId,
              municipalityKey: building.municipalityKey,
              name: building.name,
              naturalGasConsumption: ea.naturalGasConsumption,
              naturalGasFromDate: ea.naturalGasFromDate,
              naturalGasToDate: ea.naturalGasToDate,
              notes: ea.notes,
              number: building.number,
              pelletWoodConsumption: ea.pelletWoodConsumption,
              pelletWoodFromDate: ea.pelletWoodFromDate,
              pelletWoodToDate: ea.pelletWoodToDate,
              regionName: building.regionName,
              startDate: ea.startDate,
              street: building.street,
              value: eaMeter.value,
              zipCode: building.zipCode,
            });
          })
        );
      } else {
        building.meters.forEach((meter: BuildingMeter) => {
          buildingEfficiencyData.push({
            buildingTypeId: building.buildingTypeId,
            connectionObjectNumber: building.connectionObjectNumber,
            creationDate: building.creationDate,
            description: building.description,
            district: building.district,
            districtNumber: building.districtNumber,
            energyCategory: building.energyCategory,
            gpsModifiedBy: building.gpsModifiedBy,
            gpsModifyDate: building.gpsModifyDate,
            guid: building.guid,
            houseNumber: building.houseNumber,
            houseNumberAdditive: building.houseNumberAdditive,
            isEnergyGuardNotificationActive: building.isEnergyGuardNotificationActive,
            isBuildingDeactivated: isBuildingDeactivated,
            komKey: meter.komKey,
            latitude: building.latitude,
            locationInfo: building.locationInfo,
            longitude: building.longitude,
            meterNumber: meter.meterNumber,
            modifyDate: building.modifyDate,
            modifyUser: building.modifyUser,
            municipality: building.municipality,
            municipalityId: building.municipalityId,
            municipalityKey: building.municipalityKey,
            name: building.name,
            number: building.number,
            regionName: building.regionName,
            street: building.street,
            zipCode: building.zipCode,
          } as IBuildingEfficiencyData);
        });
      }
    });
    return buildingEfficiencyData;
  }
}
